import { useState, useEffect } from "react";
import { 
  Lock, 
  LogOut, 
  Package, 
  DollarSign, 
  TrendingUp,
  Download,
  Truck,
  CheckCircle,
  Clock,
  X,
  RefreshCw,
  Eye,
  Mail,
  Filter,
  FileDown,
  MapPin,
  Image,
  ExternalLink,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterType, setFilterType] = useState("all"); // all, digital, print
  const [filterStatus, setFilterStatus] = useState("all");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        localStorage.setItem('adminAuth', 'true');
        toast.success("Welcome to the admin panel!");
        fetchOrders();
      }
    } catch (error) {
      toast.error("Invalid password");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuth');
  };

  useEffect(() => {
    if (localStorage.getItem('adminAuth') === 'true') {
      setIsAuthenticated(true);
      fetchOrders();
    }
  }, []);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("order_type", filterType);
      if (filterStatus !== "all") params.append("status", filterStatus);
      
      const response = await axios.get(`${API}/admin/orders?${params.toString()}`);
      setOrders(response.data.orders);
      setStats(response.data.stats);
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [filterType, filterStatus]);

  const updateOrderStatus = async (orderId, newStatus, tracking = null) => {
    try {
      const payload = { status: newStatus };
      if (tracking?.tracking_number) payload.tracking_number = tracking.tracking_number;
      if (tracking?.tracking_url) payload.tracking_url = tracking.tracking_url;
      if (tracking?.notes) payload.notes = tracking.notes;
      
      await axios.put(`${API}/admin/orders/${orderId}/status`, payload);
      toast.success(`Order marked as ${newStatus}`);
      fetchOrders();
      setSelectedOrder(null);
      setTrackingNumber("");
      setTrackingUrl("");
      setAdminNotes("");
    } catch (error) {
      toast.error("Failed to update order");
    }
  };

  const resendCustomerEmail = async (orderId) => {
    try {
      toast.info("Sending email...");
      const response = await axios.post(`${API}/admin/orders/${orderId}/resend-email`);
      if (response.data.success) {
        toast.success(response.data.message || "Email sent successfully!");
        fetchOrders();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend email");
    }
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("order_type", filterType);
      if (filterStatus !== "all") params.append("status", filterStatus);
      
      window.location.href = `${API}/admin/orders/export?${params.toString()}`;
      toast.success("Downloading CSV...");
    } catch (error) {
      toast.error("Failed to export orders");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      paid: "bg-green-100 text-green-800",
      processing: "bg-blue-100 text-blue-800",
      shipped: "bg-purple-100 text-purple-800",
      delivered: "bg-emerald-100 text-emerald-800",
      cancelled: "bg-red-100 text-red-800",
      refunded: "bg-gray-100 text-gray-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const getProductLabel = (productType) => {
    const labels = {
      digital: "Digital Download",
      a4_print: "A4 Print",
      a3_print: "A3 Print"
    };
    return labels[productType] || productType;
  };

  const isPrintOrder = (productType) => productType === "a4_print" || productType === "a3_print";

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#f8f5f2] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto bg-[#7c5cff]/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="text-[#7c5cff]" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-[#2d2a26]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Pawtraits Admin
            </h1>
            <p className="text-[#2d2a26]/60 mt-2">Enter password to access</p>
          </div>
          
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full px-4 py-3 rounded-xl border-2 border-[#e6e0dc] focus:border-[#7c5cff] focus:ring-4 focus:ring-[#7c5cff]/20 outline-none mb-4"
              data-testid="admin-password-input"
            />
            <Button
              type="submit"
              data-testid="admin-login-btn"
              className="w-full bg-[#7c5cff] hover:bg-[#6a4de6] text-white rounded-full py-3"
            >
              Login
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-[#2d2a26]/50 hover:text-[#7c5cff]">
              ← Back to Pawtraits
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f5f2]">
      {/* Header */}
      <header className="bg-white border-b border-[#e6e0dc] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🐾</span>
            <h1 className="text-xl font-bold text-[#2d2a26]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Pawtraits Admin
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={exportCSV}
              variant="outline"
              size="sm"
              className="rounded-full"
            >
              <FileDown size={16} className="mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={fetchOrders}
              variant="outline"
              size="sm"
              className="rounded-full"
              disabled={isLoading}
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Package className="text-blue-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#2d2a26]">{stats.total_orders}</p>
              <span className="text-[#2d2a26]/60 text-sm">Total Orders</span>
            </div>
            
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="text-green-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold text-green-600">{stats.paid_orders}</p>
              <span className="text-[#2d2a26]/60 text-sm">Paid Orders</span>
            </div>
            
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <DollarSign className="text-purple-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold text-purple-600">£{stats.total_revenue.toFixed(2)}</p>
              <span className="text-[#2d2a26]/60 text-sm">Revenue</span>
            </div>
            
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Truck className="text-orange-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold text-orange-600">{stats.print_orders}</p>
              <span className="text-[#2d2a26]/60 text-sm">Print Orders</span>
            </div>
            
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="text-red-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold text-red-600">{stats.pending_shipment || 0}</p>
              <span className="text-[#2d2a26]/60 text-sm">Awaiting Shipment</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-[#2d2a26]/50" />
            <span className="text-sm font-medium text-[#2d2a26]">Filter:</span>
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm text-[#2d2a26]/60">Type:</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-[#e6e0dc] text-sm focus:border-[#7c5cff] outline-none"
            >
              <option value="all">All Orders</option>
              <option value="digital">Digital Only</option>
              <option value="print">Print Only</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm text-[#2d2a26]/60">Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-[#e6e0dc] text-sm focus:border-[#7c5cff] outline-none"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="ml-auto text-sm text-[#2d2a26]/50">
            Showing {orders.length} orders
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-[#e6e0dc]">
            <h2 className="text-lg font-bold text-[#2d2a26]">Orders</h2>
          </div>
          
          {isLoading ? (
            <div className="p-12 text-center">
              <RefreshCw className="animate-spin mx-auto text-[#7c5cff]" size={32} />
              <p className="mt-4 text-[#2d2a26]/60">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="mx-auto text-[#2d2a26]/30" size={48} />
              <p className="mt-4 text-[#2d2a26]/60">No orders found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#f8f5f2]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#2d2a26]/60 uppercase">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#2d2a26]/60 uppercase">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#2d2a26]/60 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#2d2a26]/60 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#2d2a26]/60 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#2d2a26]/60 uppercase">Address</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#2d2a26]/60 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#2d2a26]/60 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e6e0dc]">
                  {orders.map((order) => (
                    <tr key={order.id} className={`hover:bg-[#f8f5f2]/50 ${isPrintOrder(order.product_type) && order.status === 'paid' ? 'bg-orange-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {isPrintOrder(order.product_type) ? (
                            <Truck size={16} className="text-orange-500" />
                          ) : (
                            <Download size={16} className="text-green-500" />
                          )}
                          <span className="font-mono text-xs text-[#2d2a26]">
                            {order.id?.slice(0, 8)}...
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          {order.shipping_address?.full_name && (
                            <p className="font-medium text-[#2d2a26] text-sm">{order.shipping_address.full_name}</p>
                          )}
                          <a href={`mailto:${order.email}`} className="text-[#7c5cff] hover:underline text-xs">
                            {order.email || 'N/A'}
                          </a>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <span className={`font-medium text-sm ${isPrintOrder(order.product_type) ? 'text-orange-600' : 'text-green-600'}`}>
                            {getProductLabel(order.product_type)}
                          </span>
                          {order.scenario && (
                            <p className="text-xs text-[#2d2a26]/50 capitalize">{order.scenario.replace('_', ' ')}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-[#2d2a26]">£{order.price?.toFixed(2) || '0.00'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(order.status)}`}>
                          {order.status}
                        </span>
                        {order.tracking_number && (
                          <p className="text-xs text-[#2d2a26]/50 mt-1">📦 {order.tracking_number}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isPrintOrder(order.product_type) && order.shipping_address ? (
                          <div className="text-xs text-[#2d2a26]/70 max-w-[150px]">
                            <p className="truncate">{order.shipping_address.address_line_1}</p>
                            <p className="truncate">{order.shipping_address.city}, {order.shipping_address.postcode}</p>
                          </div>
                        ) : isPrintOrder(order.product_type) ? (
                          <span className="text-xs text-red-500">No address!</span>
                        ) : (
                          <span className="text-xs text-[#2d2a26]/30">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#2d2a26]/60">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedOrder(order)}
                            className="text-[#7c5cff] hover:bg-[#7c5cff]/10 h-8 w-8 p-0"
                          >
                            <Eye size={16} />
                          </Button>
                          {order.status === 'paid' && isPrintOrder(order.product_type) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedOrder({...order, showShipForm: true})}
                              className="text-orange-600 border-orange-200 hover:bg-orange-50 h-8 text-xs"
                            >
                              Ship
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
            <div className="p-6 border-b border-[#e6e0dc] flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-bold text-[#2d2a26]">
                {selectedOrder.showShipForm ? '📦 Ship Order' : 'Order Details'}
              </h3>
              <button onClick={() => {setSelectedOrder(null); setTrackingNumber(""); setTrackingUrl("");}} className="text-[#2d2a26]/50 hover:text-[#2d2a26]">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[#2d2a26]/50 uppercase">Order ID</label>
                  <p className="font-mono text-sm">{selectedOrder.id}</p>
                </div>
                <div>
                  <label className="text-xs text-[#2d2a26]/50 uppercase">Status</label>
                  <p><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(selectedOrder.status)}`}>{selectedOrder.status}</span></p>
                </div>
                <div>
                  <label className="text-xs text-[#2d2a26]/50 uppercase">Product</label>
                  <p className={`font-medium ${isPrintOrder(selectedOrder.product_type) ? 'text-orange-600' : 'text-green-600'}`}>
                    {getProductLabel(selectedOrder.product_type)}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-[#2d2a26]/50 uppercase">Amount</label>
                  <p className="font-bold text-lg text-green-600">£{selectedOrder.price?.toFixed(2)}</p>
                </div>
                <div>
                  <label className="text-xs text-[#2d2a26]/50 uppercase">Portrait Style</label>
                  <p className="capitalize">{selectedOrder.scenario?.replace('_', ' ') || 'N/A'} ({selectedOrder.art_style?.replace('_', ' ') || 'N/A'})</p>
                </div>
                <div>
                  <label className="text-xs text-[#2d2a26]/50 uppercase">Image Selected</label>
                  <p className="flex items-center gap-1">
                    <Image size={14} />
                    Variation #{(selectedOrder.selected_image_index || 0) + 1} of 4
                  </p>
                </div>
              </div>

              {/* Customer Info */}
              <div className="bg-[#f8f5f2] rounded-xl p-4">
                <h4 className="font-bold text-[#2d2a26] mb-3 flex items-center gap-2">
                  <Mail size={16} />
                  Customer
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <label className="text-xs text-[#2d2a26]/50 uppercase">Name</label>
                    <p>{selectedOrder.shipping_address?.full_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-[#2d2a26]/50 uppercase">Email</label>
                    <p><a href={`mailto:${selectedOrder.email}`} className="text-[#7c5cff] hover:underline">{selectedOrder.email}</a></p>
                  </div>
                </div>
              </div>
              
              {/* Shipping Address for Print Orders */}
              {isPrintOrder(selectedOrder.product_type) && (
                <div className={`rounded-xl p-4 ${selectedOrder.shipping_address ? 'bg-orange-50' : 'bg-red-50'}`}>
                  <h4 className="font-bold text-[#2d2a26] mb-3 flex items-center gap-2">
                    <MapPin size={16} />
                    Delivery Address
                  </h4>
                  {selectedOrder.shipping_address ? (
                    <div className="text-sm space-y-1">
                      <p className="font-medium">{selectedOrder.shipping_address.full_name}</p>
                      <p>{selectedOrder.shipping_address.address_line_1}</p>
                      {selectedOrder.shipping_address.address_line_2 && <p>{selectedOrder.shipping_address.address_line_2}</p>}
                      <p>{selectedOrder.shipping_address.city}{selectedOrder.shipping_address.county ? `, ${selectedOrder.shipping_address.county}` : ''}</p>
                      <p className="font-bold">{selectedOrder.shipping_address.postcode}</p>
                      <p>{selectedOrder.shipping_address.country || 'United Kingdom'}</p>
                    </div>
                  ) : (
                    <p className="text-red-600 font-medium">⚠️ No shipping address provided! Contact customer.</p>
                  )}
                </div>
              )}

              {/* Tracking Info */}
              {selectedOrder.tracking_number && (
                <div className="bg-purple-50 rounded-xl p-4">
                  <h4 className="font-bold text-[#2d2a26] mb-3 flex items-center gap-2">
                    <Truck size={16} />
                    Tracking Information
                  </h4>
                  <p className="font-mono text-sm">{selectedOrder.tracking_number}</p>
                  {selectedOrder.tracking_url && (
                    <a href={selectedOrder.tracking_url} target="_blank" rel="noopener noreferrer" className="text-[#7c5cff] hover:underline text-sm flex items-center gap-1 mt-2">
                      Track Package <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              )}

              {/* Ship Form */}
              {selectedOrder.showShipForm && selectedOrder.status === 'paid' && isPrintOrder(selectedOrder.product_type) && (
                <div className="bg-blue-50 rounded-xl p-4">
                  <h4 className="font-bold text-[#2d2a26] mb-4">Add Tracking Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-[#2d2a26]/70 uppercase mb-1 block">Tracking Number *</label>
                      <input
                        type="text"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        placeholder="e.g., RM123456789GB"
                        className="w-full px-3 py-2 rounded-lg border border-[#e6e0dc] text-sm focus:border-[#7c5cff] outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2d2a26]/70 uppercase mb-1 block">Tracking URL (optional)</label>
                      <input
                        type="url"
                        value={trackingUrl}
                        onChange={(e) => setTrackingUrl(e.target.value)}
                        placeholder="https://www.royalmail.com/track/..."
                        className="w-full px-3 py-2 rounded-lg border border-[#e6e0dc] text-sm focus:border-[#7c5cff] outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2d2a26]/70 uppercase mb-1 block">Notes (optional)</label>
                      <textarea
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        placeholder="Any notes about this shipment..."
                        rows={2}
                        className="w-full px-3 py-2 rounded-lg border border-[#e6e0dc] text-sm focus:border-[#7c5cff] outline-none resize-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Info */}
              <div className="text-sm">
                <div className="flex justify-between py-2 border-b border-[#e6e0dc]">
                  <span className="text-[#2d2a26]/60">Created</span>
                  <span>{selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString() : 'N/A'}</span>
                </div>
                {selectedOrder.stripe_session_id && (
                  <div className="flex justify-between py-2">
                    <span className="text-[#2d2a26]/60">Stripe Session</span>
                    <span className="font-mono text-xs max-w-[200px] truncate">{selectedOrder.stripe_session_id}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Actions */}
            <div className="p-6 border-t border-[#e6e0dc] flex flex-wrap gap-3">
              {selectedOrder.showShipForm && selectedOrder.status === 'paid' && isPrintOrder(selectedOrder.product_type) ? (
                <>
                  <Button
                    onClick={() => updateOrderStatus(selectedOrder.id, 'shipped', {
                      tracking_number: trackingNumber,
                      tracking_url: trackingUrl,
                      notes: adminNotes
                    })}
                    disabled={!trackingNumber}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    <Truck size={16} className="mr-2" />
                    Mark as Shipped
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedOrder({...selectedOrder, showShipForm: false})}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  {selectedOrder.status === 'paid' && (
                    <Button
                      onClick={() => resendCustomerEmail(selectedOrder.id)}
                      className="bg-blue-500 hover:bg-blue-600"
                      data-testid="resend-email-btn"
                    >
                      <Mail size={16} className="mr-2" />
                      Resend Customer Email
                    </Button>
                  )}
                  {selectedOrder.status === 'paid' && isPrintOrder(selectedOrder.product_type) && (
                    <Button
                      onClick={() => setSelectedOrder({...selectedOrder, showShipForm: true})}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      <Truck size={16} className="mr-2" />
                      Ship Order
                    </Button>
                  )}
                  {selectedOrder.status === 'shipped' && (
                    <Button
                      onClick={() => updateOrderStatus(selectedOrder.id, 'delivered', {})}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle size={16} className="mr-2" />
                      Mark Delivered
                    </Button>
                  )}
                  {selectedOrder.status === 'pending' && (
                    <Button
                      onClick={() => updateOrderStatus(selectedOrder.id, 'cancelled', {})}
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      Cancel Order
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setSelectedOrder(null)}
                    className="ml-auto"
                  >
                    Close
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
